import { useEffect, useMemo, useState } from 'react';
import type {
  CustomDateRange,
  TimeRange,
} from '../../components/dialogs/TimeRangeSelector';
import { resolveTimeBounds } from '../../components/dialogs/timeRangeDomain';
import {
  useGetMachineGroupedDataQuery,
  type GetMachineGroupedDataQuery,
  type Machine,
} from '../../gql/generated';
import {
  CRITERION_DIMENSION,
  sortingSource,
  STREAM_DIMENSION,
} from './machineDataSources';
import type { EjectorShare, OutcomeShare } from './machineSimulation';
import type { AcceptRatePoint, SortingView } from './sortingView';

const MINUTE_MS = 60_000;

/** EDMS stores counts per minute, so a fresher fetch shows nothing new. */
const REFRESH_MS = MINUTE_MS;

/** The accept-rate graph asks EDMS for about this many buckets. */
const RATE_BUCKETS = 24;

/** An ejector lists this many reasons; the rest share one row. */
const MAX_REASONS = 6;

const objectCount = new Intl.NumberFormat('de-DE');

type DataItem = NonNullable<
  GetMachineGroupedDataQuery['machineData']
>['items'][number];

function dimension(item: DataItem, name: string): string {
  return item.dimensions?.find((d) => d.name === name)?.value ?? '';
}

/** The stream of good product. Every other stream is an ejector. */
function isAcceptStream(streamName: string): boolean {
  return /^accept/i.test(streamName);
}

/** The current minute. It moves while `live`, so the window slides along. */
function useMinuteNow(live: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => setNow(Date.now()), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [live]);
  return Math.floor(now / MINUTE_MS) * MINUTE_MS;
}

function toView(
  streams: DataItem[],
  criteria: DataItem[],
  series: DataItem[]
): SortingView | null {
  const total = streams.reduce((sum, item) => sum + (item.sum ?? 0), 0);
  if (total <= 0) return null;

  const share = (count: number) => (count / total) * 100;

  const reasonsOf = (streamName: string): OutcomeShare[] => {
    const reasons = criteria
      .filter((item) => dimension(item, STREAM_DIMENSION) === streamName)
      .map((item) => ({
        label: dimension(item, CRITERION_DIMENSION),
        count: item.sum ?? 0,
      }))
      .filter((reason) => reason.count > 0)
      .sort((a, b) => b.count - a.count);
    const shown = reasons.slice(0, MAX_REASONS);
    const rest = reasons
      .slice(MAX_REASONS)
      .reduce((sum, reason) => sum + reason.count, 0);
    return [
      ...shown.map((reason) => ({
        label: reason.label,
        percent: share(reason.count),
      })),
      ...(rest > 0 ? [{ label: 'Other criteria', percent: share(rest) }] : []),
    ];
  };

  let accepted = 0;
  const ejectors: EjectorShare[] = [];
  for (const item of streams) {
    const streamName = dimension(item, STREAM_DIMENSION);
    const count = item.sum ?? 0;
    if (isAcceptStream(streamName)) {
      accepted += count;
    } else {
      ejectors.push({
        label: streamName,
        percent: share(count),
        reasons: reasonsOf(streamName),
      });
    }
  }

  const buckets = new Map<string, { accepted: number; total: number }>();
  for (const item of series) {
    const bucket = buckets.get(item.timeUtc) ?? { accepted: 0, total: 0 };
    const count = item.sum ?? 0;
    bucket.total += count;
    if (isAcceptStream(dimension(item, STREAM_DIMENSION))) {
      bucket.accepted += count;
    }
    buckets.set(item.timeUtc, bucket);
  }
  const acceptRateSeries: AcceptRatePoint[] = [...buckets.entries()]
    .map(([time, bucket]) => ({
      time: new Date(time).getTime(),
      percent: bucket.total > 0 ? (bucket.accepted / bucket.total) * 100 : null,
    }))
    .sort((a, b) => a.time - b.time);

  return {
    accepted: share(accepted),
    ejectors,
    sorted: `${objectCount.format(total)} objects`,
    acceptRateSeries,
  };
}

/**
 * The Sorting tab's figures from EDMS: objects per stream, why the ejector
 * fired, and the acceptance rate over time, for the chosen window.
 */
export function useMachineSortingView(
  machine: Machine,
  timeRange: TimeRange,
  customRange: CustomDateRange | undefined,
  running: boolean
) {
  const source = useMemo(() => sortingSource(machine), [machine]);
  // A window that ends in the past keeps its end, so its figures stay put.
  const now = useMinuteNow(running);
  const reachesNow =
    timeRange !== 'custom' || !customRange || customRange.to.getTime() >= now;

  const bounds = resolveTimeBounds(timeRange, customRange, now);
  const startTime = new Date(bounds.from).toISOString();
  const endTime = new Date(Math.min(bounds.to, now)).toISOString();
  const base = {
    id: machine.id,
    startTime,
    endTime,
  };
  const skip = !source;

  const streamsQuery = useGetMachineGroupedDataQuery({
    variables: {
      ...base,
      signalCategory: source?.streams.category ?? '',
      signal: source?.streams.signal ?? '',
      dimensions: [STREAM_DIMENSION],
      overTime: false,
    },
    skip,
  });
  const criteriaQuery = useGetMachineGroupedDataQuery({
    variables: {
      ...base,
      signalCategory: source?.criteria?.category ?? '',
      signal: source?.criteria?.signal ?? '',
      dimensions: [STREAM_DIMENSION, CRITERION_DIMENSION],
      overTime: false,
    },
    skip: skip || !source?.criteria,
  });
  const seriesQuery = useGetMachineGroupedDataQuery({
    variables: {
      ...base,
      signalCategory: source?.streams.category ?? '',
      signal: source?.streams.signal ?? '',
      dimensions: [STREAM_DIMENSION],
      overTime: true,
      numDataPoints: RATE_BUCKETS,
    },
    skip,
  });

  // A sliding window refetches every minute. The previous figures stay up
  // until the new ones arrive.
  const streams = (streamsQuery.data ?? streamsQuery.previousData)?.machineData
    ?.items;
  const criteria = (criteriaQuery.data ?? criteriaQuery.previousData)
    ?.machineData?.items;
  const series = (seriesQuery.data ?? seriesQuery.previousData)?.machineData
    ?.items;

  const view = useMemo(
    () => (streams ? toView(streams, criteria ?? [], series ?? []) : null),
    [streams, criteria, series]
  );

  return {
    view,
    loading: !streams && streamsQuery.loading,
    error: streamsQuery.error,
    live: running && reachesNow,
  };
}
